/* Hallmark · component: form · genre: modern-minimal · theme: project-tokens
 * states: default · hover · focus · active · disabled · loading · error · success
 * Rack settings — schema fields only (RackConfigUpdate). No fake `name`.
 */
import React, { useState } from 'react';
import { Save, ShieldAlert, Server } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { FormDrawer } from '../ui/FormDrawer';
import { rackUpdatePayload, rackDisplayTitle } from '../../utils/rackEdit.logic';

const inputCls = `
  w-full px-3 py-2 rounded-sm text-sm font-mono
  focus:outline-none focus-visible:ring-1
`

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[10px] uppercase tracking-[0.1em] font-semibold mb-1.5"
      style={{ color: 'var(--text-muted)' }}
    >
      {children}
    </label>
  )
}

function SectionDiv({ label }: { label: string }) {
  return (
    <div
      className="flex items-center gap-3 py-1"
      style={{ borderTop: '1px solid var(--border-color)', marginTop: 8 }}
    >
      <span
        className="text-[9px] uppercase tracking-[0.12em] shrink-0"
        style={{ color: 'var(--text-muted)' }}
      >
        {label}
      </span>
    </div>
  )
}

interface RackEditModalProps {
  rack: any;
  datacenterCode: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const RackEditModal: React.FC<RackEditModalProps> = ({
  rack,
  onClose,
  onSuccess,
}) => {
  const [heightU, setHeightU] = useState<number>(
    Number(rack?.u_slots || (rack?.height_m ? Math.round(rack.height_m / 0.04445) : 42))
  )
  const [type, setType] = useState(rack?.type || 'server_rack')
  const [isBlocked, setIsBlocked] = useState(Boolean(rack?.is_blocked))
  const [tenant, setTenant] = useState(rack?.tenant || 'mine')
  const [cascadeTenant, setCascadeTenant] = useState(true)
  const [maxWeightKg, setMaxWeightKg] = useState<number>(Number(rack?.max_weight_kg ?? 1000))
  const [maxPowerW, setMaxPowerW] = useState<number>(Number(rack?.max_power_w ?? 5000))
  const [widthM, setWidthM] = useState<number>(Number(rack?.width_m ?? 0.6))
  const [depthM, setDepthM] = useState<number>(Number(rack?.depth_m ?? 1.0))
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.put(`/api/datacenters/racks/${rack.id}`, rackUpdatePayload({
        type,
        heightU,
        isBlocked,
        tenant,
        cascadeTenant,
        maxWeightKg,
        maxPowerW,
        widthM,
        depthM,
      }))
      toast.success('Параметры стойки обновлены')
      onSuccess()
      onClose()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Не удалось обновить стойку')
    } finally {
      setSaving(false)
    }
  }

  const rackTitle = rackDisplayTitle(rack)

  return (
    <FormDrawer
      title="Параметры стойки"
      subtitle={rackTitle}
      isOpen={true}
      onClose={onClose}
      maxWidth={440}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs rounded-sm transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            Отмена
          </button>
          <button
            type="submit"
            form="rack-edit-form"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-sm transition-colors disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            <Save size={13} aria-hidden />
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <form id="rack-edit-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Server size={12} style={{ color: 'var(--accent)' }} aria-hidden />
            <span className="text-[10px] uppercase tracking-[0.1em] font-semibold" style={{ color: 'var(--text-muted)' }}>
              Идентификация
            </span>
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-label">Метка (шаблон ЦОД)</FieldLabel>
            <input
              id="rack-edit-label"
              type="text"
              value={rackTitle}
              readOnly
              className={inputCls}
              style={{ ...inputStyle, opacity: 0.85 }}
            />
          </div>
        </div>

        <SectionDiv label="Характеристики" />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel htmlFor="rack-edit-height">Высота (U)</FieldLabel>
            <select
              id="rack-edit-height"
              value={heightU}
              onChange={e => setHeightU(Number(e.target.value))}
              className={inputCls}
              style={inputStyle}
            >
              <option value={24}>24 U</option>
              <option value={42}>42 U</option>
              <option value={45}>45 U</option>
              <option value={48}>48 U</option>
              <option value={52}>52 U</option>
            </select>
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-type">Тип стойки</FieldLabel>
            <select
              id="rack-edit-type"
              value={type}
              onChange={e => setType(e.target.value)}
              className={inputCls}
              style={inputStyle}
            >
              <option value="server_rack">Серверная</option>
              <option value="network_rack">Сетевая</option>
              <option value="telecom">Телеком шкаф</option>
              <option value="pdu_frame">PDU Frame</option>
            </select>
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-width">Ширина (м)</FieldLabel>
            <input id="rack-edit-width" type="number" step="0.01" min="0.1" value={widthM}
              onChange={e => setWidthM(Number(e.target.value))} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-depth">Глубина (м)</FieldLabel>
            <input id="rack-edit-depth" type="number" step="0.01" min="0.1" value={depthM}
              onChange={e => setDepthM(Number(e.target.value))} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-weight">Макс. масса (кг)</FieldLabel>
            <input id="rack-edit-weight" type="number" min="1" value={maxWeightKg}
              onChange={e => setMaxWeightKg(Number(e.target.value))} className={inputCls} style={inputStyle} />
          </div>
          <div>
            <FieldLabel htmlFor="rack-edit-power">Макс. мощность (Вт)</FieldLabel>
            <input id="rack-edit-power" type="number" min="1" value={maxPowerW}
              onChange={e => setMaxPowerW(Number(e.target.value))} className={inputCls} style={inputStyle} />
          </div>
          <div className="col-span-2">
            <FieldLabel htmlFor="rack-edit-tenant">Тенант</FieldLabel>
            <select id="rack-edit-tenant" value={tenant} onChange={e => setTenant(e.target.value)}
              className={inputCls} style={inputStyle}>
              <option value="mine">mine</option>
              <option value="other">other</option>
              <option value="reserved">reserved</option>
            </select>
            <label className="flex items-center gap-2 mt-2 cursor-pointer text-xs text-slate-300">
              <input
                type="checkbox"
                checked={cascadeTenant}
                onChange={e => setCascadeTenant(e.target.checked)}
                className="w-3.5 h-3.5 accent-cyan-500 rounded"
              />
              <span>Применить владельца ко всему установленному оборудованию</span>
            </label>
          </div>
        </div>

        <SectionDiv label="Доступность" />

        <label
          htmlFor="rack-edit-blocked"
          className="flex items-start gap-3 p-3 rounded-sm cursor-pointer transition-colors"
          style={{
            background: isBlocked ? 'rgba(139,92,246,0.06)' : 'var(--bg-app)',
            border: `1px solid ${isBlocked ? 'rgba(139,92,246,0.3)' : 'var(--border-color)'}`,
          }}
        >
          <input
            id="rack-edit-blocked"
            type="checkbox"
            checked={isBlocked}
            onChange={e => setIsBlocked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded-sm shrink-0"
            style={{ accentColor: 'var(--status-blocked)' }}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldAlert size={12} style={{ color: isBlocked ? 'var(--status-blocked)' : 'var(--text-muted)' }} aria-hidden />
              <span className="text-xs font-semibold" style={{ color: isBlocked ? 'var(--status-blocked)' : 'var(--text-secondary)' }}>
                Заблокировать стойку
              </span>
            </div>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Запретит установку нового оборудования в эту стойку
            </p>
          </div>
        </label>
      </form>
    </FormDrawer>
  )
}
